/**
 * Runtime font loading — typefaces hosted in Library Admin rather than
 * bundled into this package.
 *
 * ## Why
 *
 * The seven bundled families are 5 MB of MSDF atlases, and every consumer
 * pays all of it to render one paragraph. Every further family a design asks
 * for costs roughly another 640 KB of package. Hosting decouples the two: a
 * family becomes an upload to the deployment's own library, and the package
 * keeps only the faces that must render before any network call resolves.
 *
 * ## What a pack is
 *
 * An MSDF face is a BMFont JSON + PNG pair, so a family is a small manifest
 * listing its cuts and where each half lives:
 *
 * ```json
 * { "family": "Space Mono", "revision": 1, "charset": "latin1+punct",
 *   "faces": [ { "weight": "regular", "style": "normal",
 *                "definitionUrl": "https://…/space-mono-regular.json",
 *                "textureUrl":    "https://…/space-mono-regular.png" } ] }
 * ```
 *
 * Nothing in this package knows where those URLs point. The engine ships no
 * pack list at all: the host declares the families its deployment publishes,
 * at boot, with {@link declareFontPacks}. A font catalogue is deployment
 * data, and a library URL baked into a published package would follow every
 * consumer home.
 *
 * ## What registering a face actually does
 *
 * Two installs, both or neither:
 *
 * 1. **Asset URLs** → `fontRegistry`, so the renderer fetches the right atlas.
 * 2. **Measurement metrics** → `fontMetricsRegistry`, derived from the same
 *    BMFont JSON via `bmfontToMetrics`.
 *
 * Doing only (1) is the interesting failure: the paragraph would RENDER in the
 * hosted face while MEASURING as Roboto, so every line break, every Hug
 * container and every export would be laid out for a face that is not on
 * screen. That is strictly worse than the visible substitution this workstream
 * exists to remove, which is why `registerFace` derives the metrics itself and
 * never takes them on trust.
 *
 * ## Late arrival
 *
 * Text is measured from the metrics table, synchronously, whether or not any
 * atlas has loaded — so a paragraph naming a not-yet-loaded family measures
 * through the fallback chain and is laid out immediately. It must not then
 * stay that way: {@link onFontRegistryChange} fires on every successful
 * registration, and a Text whose resolved face just changed re-measures,
 * re-solves its layout, and reloads its renderer (one drawing in another cut
 * of the family is left alone). Nothing polls.
 *
 * ## Who supplies the URLs
 *
 * The host does. A pack manifest and every URL in it are fetched as given, so
 * they must never come from a scene or from user input (a scene names a family
 * and nothing else), and the page has to be allowed to read them (CORS).
 *
 * ## Bundled wins
 *
 * A pack can add faces; it can never replace one that shipped. Both registries
 * refuse a bundled key, so no deployment can change how an existing scene
 * renders by uploading something.
 *
 * ## One catalogue per page
 *
 * Declarations, registered faces and listeners are module state, so they belong
 * to the PAGE, not to a viewer. Every `<trace-viewer>` on a page, and every
 * scene in one, sees the same fonts: a pack declared for one is declared for
 * all, and two viewers cannot be given different catalogues. That differs from
 * a per-viewer setting, which is an attribute on each element and may differ
 * between two viewers on one page. A host that embeds viewers for different
 * libraries on one page declares the union, and a family name has to mean one
 * face across all of them.
 *
 * ## A re-cut
 *
 * A scene names a family, not a cut, so what it draws with is whatever the
 * deployment has declared NOW. Declaring a pack whose `revision` differs from
 * the one a face was installed at re-fetches that face, replaces its metrics
 * and URLs, and announces it like any other arrival: paragraphs already laid
 * out in the old cut re-measure and re-solve. That is deliberate — the
 * alternative is a page drawing two cuts under one name — and it is why a
 * re-cut publishes new URLs rather than overwriting the old ones.
 */
import { Logger } from '../../utils/Logger';
import type { FontWeight, FontStyle } from '../../scenedata/behaviors/textdata';
import {
    declareFontVariant,
    fontRegistryKey,
    hasFontAssetUrls,
    isVariantBundled,
    registerFontAssetUrls,
    undeclareFamilyVariants,
    type FontKey,
} from './fontRegistry';
import { registerFaceMetrics } from './fontMetricsRegistry';
import { bmfontToMetrics, parseBmFont, type BmFontJson } from './bmfont';

/** One cut of a hosted family: where its definition and atlas live. */
export interface HostedFontFace {
    /** Defaults to `'regular'` — the slot the fallback chain lands on. */
    weight?: FontWeight;
    /** Defaults to `'normal'`. */
    style?: FontStyle;
    /** URL of the BMFont JSON definition. */
    definitionUrl: string;
    /** URL of the MSDF atlas PNG. */
    textureUrl: string;
}

/** A family's faces, as published by Library Admin. */
export interface HostedFontPack {
    family: string;
    /**
     * Bumped whenever the pack is re-cut. The engine keys faces by
     * `family|weight|style`, so this is what distinguishes "the same face"
     * from "the same name, different metrics" — the thing the package version
     * used to answer for bundled atlases and can no longer answer for these.
     *
     * Loading a face at a different revision than the one installed replaces
     * it, and the paragraphs drawn in it re-measure. A load that names no
     * revision takes whatever is installed, and a pack without one can never
     * be told apart from itself, so re-declaring it changes nothing.
     */
    revision?: number;
    /**
     * Which characters the atlases cover, e.g. `'latin1+punct'`. The bundled
     * faces share one charset by construction; hosted packs will not, and a
     * paragraph measured against a face missing `—` measures wrong in a way no
     * version number catches. Logged on load so it is diagnosable.
     */
    charset?: string;
    faces: readonly HostedFontFace[];
}

/**
 * Why a face in a pack did not register. Three genuinely different outcomes,
 * because a host acts on them differently: nothing to do, a pack that is
 * wrong about what it publishes, and a load worth trying again.
 */
export type HostedFontSkipReason =
    /** This face was already registered, at this revision. Nothing to do. */
    | 'already-registered'
    /**
     * The same key ships inside the package, so the bundled face wins and the
     * pack's URLs were refused. A pack listing it is describing a face it does
     * not own; the paragraph renders, from the bundled atlas.
     */
    | 'bundled'
    /**
     * The definition or the atlas did not arrive (an error status, or no
     * answer within {@link HOSTED_FONT_FETCH_TIMEOUT_MS}), or the definition was
     * not a usable BMFont document. The only reason that is worth retrying —
     * {@link retryFontFamily}.
     */
    | 'fetch-failed';

/** What a load did, per face. */
export interface HostedFontLoadResult {
    family: string;
    /** Faces newly registered by this call. */
    registered: FontKey[];
    /** Faces that were skipped or failed, with why. */
    skipped: Array<{ key: FontKey; reason: HostedFontSkipReason }>;
}

// The closed sets a manifest's `weight` / `style` must come from. Built from a
// `Record` over the type, so adding a member to `FontWeight` / `FontStyle`
// fails the build here instead of silently refusing the new value.
const FONT_WEIGHTS: readonly string[] = Object.keys({ regular: true, bold: true } satisfies Record<FontWeight, true>);
const FONT_STYLES: readonly string[] = Object.keys({ normal: true, italic: true } satisfies Record<FontStyle, true>);

type ChangeListener = (family: string, key: FontKey) => void;

const listeners = new Set<ChangeListener>();

// Loads under way, keyed by `family|weight|style`. Two Text nodes naming the
// same hosted family come up at the same time; without this they would each
// fetch the same definition. The revision rides along because a load for
// another revision of the same face is a different load.
const inFlight = new Map<string, { revision: number | undefined; promise: Promise<boolean> }>();
// What is installed, and the revision it was installed at (when it named one).
// A finished load is a no-op the second time rather than a second fetch.
const registered = new Set<string>();
const revisions = new Map<string, number>();

/**
 * Is this face already installed at the revision being asked for? A caller
 * that names none takes whatever is installed; one that names another revision
 * than the installed one is asking for a re-cut.
 */
function isCurrent(k: string, revision: number | undefined): boolean {
    if (!registered.has(k)) return false;
    return revision === undefined || revisions.get(k) === revision;
}

/**
 * Subscribe to face registrations. Returns an unsubscribe function.
 *
 * Fires once per face actually installed — not for a skipped or failed one,
 * and not for a re-registration of something already present, so a listener
 * can treat every call as "this changed, act on it".
 */
export function onFontRegistryChange(listener: ChangeListener): () => void {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
}

/**
 * Register an already-fetched face. Synchronous, and the only place a face
 * enters either registry.
 *
 * `definition` is the parsed BMFont JSON — the same document the renderer will
 * fetch from `face.definitionUrl`. It is passed in rather than fetched here so
 * that a host with its own asset pipeline (a bake, a cached bundle, a test)
 * can register without a network round trip.
 */
export function registerFace(
    family: string,
    face: HostedFontFace,
    definition: BmFontJson,
    revision?: number,
): boolean {
    const key = keyFor(family, face);
    const k = fontRegistryKey(key);

    // Both halves or neither. Metrics go in first, so a refusal from the URL
    // registry has something to undo: a face that measured as the hosted cut
    // and drew as another, or a re-cut whose new metrics outlived its refused
    // URLs, is the failure the module doc calls out. `undoMetrics` restores what
    // was there (nothing, or the cut this one replaced).
    const undoMetrics = registerFaceMetrics(k, bmfontToMetrics(definition));
    if (!undoMetrics) return false;
    if (!registerFontAssetUrls(key, {
        definitionUrl: face.definitionUrl,
        textureUrl: face.textureUrl,
    })) {
        undoMetrics();
        return false;
    }

    registered.add(k);
    // An install that names no revision leaves the face's revision unknown, not
    // whatever the previous cut said.
    if (revision !== undefined) revisions.set(k, revision);
    else revisions.delete(k);
    for (const listener of listeners) {
        try {
            listener(family, key);
        } catch (error) {
            // A misbehaving listener must not abort the remaining ones, nor
            // leave the face half-announced.
            Logger.warn(`[Text] font registry listener threw: ${String(error)}`);
        }
    }
    return true;
}

/**
 * Fetch one face's BMFont definition and register it.
 *
 * Resolves true when the face is registered as a result of this call.
 * Concurrent callers for the same face share one fetch and one answer.
 *
 * A face already installed at the requested revision resolves false without
 * fetching, and so does a BUNDLED one: registration would refuse it anyway, so
 * the download would be thrown away. A different revision than the installed
 * one is a re-cut and is fetched and installed over the old one.
 *
 * Never throws and never rejects: a bad URL, a 404 on the definition or on the
 * atlas, an HTML error page served in place of JSON, a host that never answers
 * ({@link HOSTED_FONT_FETCH_TIMEOUT_MS}) — all resolve `false` and leave the
 * paragraph on the fallback chain, which is a degraded render rather than a
 * dead scene. The atlas's body is not downloaded here (its status is checked,
 * so a face whose PNG 404s counts as failed); the renderer loads it as a
 * texture when the behavior next reloads.
 */
export async function loadFace(
    family: string,
    face: HostedFontFace,
    revision?: number,
): Promise<boolean> {
    const key = keyFor(family, face);
    const k = fontRegistryKey(key);
    if (isVariantBundled(key)) return false;
    if (isCurrent(k, revision)) return false;
    if (!hasFontAssetUrls(face)) {
        // Nothing to fetch: `fetch(undefined)` asks for the URL "undefined".
        Logger.warn(`[Text] hosted font "${family}" has a face without a definitionUrl or textureUrl`);
        return false;
    }

    const existing = inFlight.get(k);
    if (existing && existing.revision === revision) return existing.promise;

    // A load for ANOTHER revision of this face is still on the wire: let it
    // land first, so this one — the one asked for last — is what stays
    // installed whichever answer would have come back first.
    const start = (): Promise<boolean> => isCurrent(k, revision)
        ? Promise.resolve(false)
        : fetchAndRegister(family, face, revision);
    const load = existing ? existing.promise.then(start) : start();

    inFlight.set(k, { revision, promise: load });
    void load.then(() => {
        if (inFlight.get(k)?.promise === load) inFlight.delete(k);
    });
    return load;
}

/**
 * How long a hosted-font request may take before it counts as failed: one
 * definition or manifest, body included, or one atlas answering at all.
 *
 * Without a bound a host that accepts the connection and never answers leaves
 * the request pending for the life of the page: `ensureFontFamily` never
 * settles, the family never reaches `onHostedFontFailure` /
 * `hostedFontFailures()`, and `retryFontFamily` has nothing to retry because
 * nothing failed. The paragraph would sit on the fallback with no signal at all
 * — the silent substitution this module exists to remove.
 *
 * 20 s because the bound covers the whole download: the largest definition is
 * ~870 KB, and at 50 KB/s (a weak mobile link) that is ~17 s. A hung host is
 * the case this catches, not a slow one. A timeout is a `fetch-failed`, which
 * the host retries when it chooses ({@link retryFontFamily}).
 */
export const HOSTED_FONT_FETCH_TIMEOUT_MS = 20_000;

/**
 * `fetch` + `read`, rejecting if the whole exchange has not finished within
 * {@link HOSTED_FONT_FETCH_TIMEOUT_MS}. The request is aborted as well as
 * abandoned, so a stalled connection is released rather than left open; the
 * race is what makes the bound hold for a `fetch` that ignores its signal.
 */
async function fetchWithin<T>(
    url: string,
    read: (response: Response) => Promise<T>,
): Promise<T> {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const limit = new Promise<never>((_, reject) => {
        timer = setTimeout(() => {
            controller.abort();
            reject(new Error(`no answer within ${HOSTED_FONT_FETCH_TIMEOUT_MS / 1000}s`));
        }, HOSTED_FONT_FETCH_TIMEOUT_MS);
    });
    try {
        return await Promise.race([
            fetch(url, { signal: controller.signal }).then(read),
            limit,
        ]);
    } finally {
        clearTimeout(timer);
    }
}

/** The definition's status, and its text when it arrived. */
function readDefinition(response: Response): Promise<{ status: number; text: string | null }> {
    if (!response.ok) return Promise.resolve({ status: response.status, text: null });
    return response.text().then((text) => ({ status: response.status, text }));
}

/**
 * The atlas's status. Only the status: the renderer downloads the PNG itself
 * when a paragraph draws in this face, and a family loads every cut of a pack
 * although a paragraph usually draws one, so reading each body here would
 * fetch megabytes nobody asked for. The request is cancelled once the headers
 * are in.
 */
function readAtlasStatus(response: Response): Promise<number> {
    void response.body?.cancel().catch(() => undefined);
    return Promise.resolve(response.status);
}

async function fetchAndRegister(
    family: string,
    face: HostedFontFace,
    revision: number | undefined,
): Promise<boolean> {
    try {
        // Both halves of the face are checked before either registry changes.
        // The atlas is what the renderer loads as a texture with no failure
        // path of its own (Babylon logs a 404 and draws the glyphs blank), so
        // a definition that parses beside an atlas that 404s would register as
        // a success, and the paragraph would leave the fallback chain for an
        // invisible font — with nothing on `onHostedFontFailure`.
        const [definitionAnswer, atlasStatus] = await Promise.all([
            fetchWithin(face.definitionUrl, readDefinition),
            fetchWithin(face.textureUrl, readAtlasStatus),
        ]);
        if (definitionAnswer.text === null) {
            Logger.warn(
                `[Text] hosted font "${family}" definition fetch failed ` +
                `(${definitionAnswer.status}): ${face.definitionUrl}`,
            );
            return false;
        }
        if (atlasStatus < 200 || atlasStatus >= 300) {
            Logger.warn(
                `[Text] hosted font "${family}" atlas fetch failed ` +
                `(${atlasStatus}): ${face.textureUrl}`,
            );
            return false;
        }
        const definition = parseBmFont(definitionAnswer.text);
        if (!definition) {
            Logger.warn(
                `[Text] hosted font "${family}" definition is not a usable ` +
                `BMFont document: ${face.definitionUrl}`,
            );
            return false;
        }
        return registerFace(family, face, definition, revision);
    } catch (error) {
        Logger.warn(`[Text] hosted font "${family}" failed to load: ${String(error)}`);
        return false;
    }
}

/**
 * Load every face in a pack. Faces load concurrently and independently — one
 * bad URL costs that cut, not the family, and the family's other cuts still
 * carry the paragraph through the weight/style fallback chain.
 */
export async function loadFontPack(pack: HostedFontPack): Promise<HostedFontLoadResult> {
    const result: HostedFontLoadResult = { family: pack.family, registered: [], skipped: [] };
    if (pack.charset) {
        Logger.debug(`[Text] hosted font "${pack.family}" charset: ${pack.charset}`);
    }

    // Snapshot before the fetches start. "Already registered" is only
    // answerable now: by the time they finish, a face registered BY this call
    // looks exactly like one that was there all along. At THIS revision: a
    // face installed from an older cut is being re-fetched, not skipped.
    const entries = pack.faces.map((face) => {
        const key = keyFor(pack.family, face);
        return { face, key, wasCurrent: isCurrent(fontRegistryKey(key), pack.revision) };
    });

    const outcomes = await Promise.all(
        entries.map(async (entry) => ({
            ...entry,
            ok: await loadFace(pack.family, entry.face, pack.revision),
        })),
    );

    for (const { key, ok, wasCurrent } of outcomes) {
        if (ok) result.registered.push(key);
        else result.skipped.push({ key, reason: skipReason(key, wasCurrent) });
    }
    return result;
}

/**
 * Why one face did not register, from what is knowable about it rather than
 * from a single catch-all string. `wasCurrent` is the caller's pre-load
 * snapshot; everything else the registries can still answer afterwards.
 */
function skipReason(key: FontKey, wasCurrent: boolean): HostedFontSkipReason {
    if (wasCurrent) return 'already-registered';
    if (isVariantBundled(key)) return 'bundled';
    return 'fetch-failed';
}

function keyFor(family: string, face: HostedFontFace): FontKey {
    return {
        family,
        weight: face.weight ?? 'regular',
        style: face.style ?? 'normal',
    };
}

/**
 * Fetch a pack manifest by URL and load it — the one-call entry a host uses at
 * boot for each family its deployment publishes.
 */
export async function loadFontPackFromUrl(url: string): Promise<HostedFontLoadResult | null> {
    try {
        const answer = await fetchWithin(url, async (response) => ({
            status: response.status,
            body: response.ok ? await response.json() as unknown : undefined,
            ok: response.ok,
        }));
        if (!answer.ok) {
            Logger.warn(`[Text] font pack fetch failed (${answer.status}): ${url}`);
            return null;
        }
        const pack = answer.body as Partial<HostedFontPack>;
        if (typeof pack?.family !== 'string' || !Array.isArray(pack.faces)) {
            Logger.warn(`[Text] font pack at ${url} is missing "family" or "faces"`);
            return null;
        }
        // Every face names both halves. A manifest that lists a cut it cannot
        // address is wrong about what it publishes, so the pack is refused
        // whole, as for a missing "family", rather than half-loaded.
        const unaddressable = pack.faces.findIndex((face) => !hasFontAssetUrls(face));
        if (unaddressable !== -1) {
            Logger.warn(
                `[Text] font pack at ${url}: face ${unaddressable} is missing ` +
                `"definitionUrl" or "textureUrl"`,
            );
            return null;
        }
        // A weight or style outside the closed sets would install under a key
        // no paragraph can ask for, so the face would load and never draw. Same
        // ruling: the pack is wrong about what it publishes.
        const unaddressableCut = (pack.faces as Array<Partial<HostedFontFace>>).findIndex((face) =>
            (face.weight !== undefined && !FONT_WEIGHTS.includes(face.weight))
            || (face.style !== undefined && !FONT_STYLES.includes(face.style)));
        if (unaddressableCut !== -1) {
            Logger.warn(
                `[Text] font pack at ${url}: face ${unaddressableCut} has a weight or style ` +
                `outside ${FONT_WEIGHTS.join('/')} and ${FONT_STYLES.join('/')}`,
            );
            return null;
        }
        return await loadFontPack(pack as HostedFontPack);
    } catch (error) {
        Logger.warn(`[Text] font pack at ${url} failed to load: ${String(error)}`);
        return null;
    }
}

/** The revision a registered face came from, if its pack declared one. */
export function registeredRevision(key: FontKey): number | undefined {
    return revisions.get(fontRegistryKey(key));
}

// ---------------------------------------------------------------------------
// The catalogue: what this deployment publishes, before any of it is fetched
// ---------------------------------------------------------------------------

const declared = new Map<string, HostedFontPack>();
/**
 * The load an ensure started for a family, kept once it has settled. A family
 * whose fetch FAILED stays here too, so the Text behavior does not re-fetch it
 * on every measure — `_resolveFace` calls `ensureFontFamily` on every measure
 * by design — and a caller awaiting it later gets the answer that attempt gave.
 * Only a retry or a re-cut replaces it.
 */
const loads = new Map<string, Promise<HostedFontLoadResult>>();
const NOTHING_TO_LOAD: Promise<null> = Promise.resolve(null);
const failures = new Map<string, HostedFontPackFailure>();
// The entries of `loads` whose attempt has finished (see isFontFamilyLoading).
const settledLoads = new WeakSet<Promise<HostedFontLoadResult>>();

/**
 * Announce the families this deployment publishes, without fetching any of
 * them.
 *
 * This is what makes hosting work at all rather than just work in principle.
 * `loadFontPack` alone would mean either fetching every published family's
 * definition at boot — several MB, to answer a question most pages never ask
 * — or answering "no, we cannot draw that" right up until something did, which
 * is exactly when the converter has already dropped the name and written the
 * theme binding instead. Declaring is the third option: the catalogue is known
 * synchronously and costs a few KB of URLs; the bytes arrive when a paragraph
 * actually names the family ({@link ensureFontFamily}).
 *
 * Additive and idempotent. Re-declaring a family replaces its pack (a later
 * call wins for the same name) but never un-declares a face — that is
 * {@link undeclareFontFamily} — and it can never shadow a bundled one:
 * `declareFontVariant` refuses those.
 *
 * Re-declaring a family at a different `revision` is a re-cut, and is acted on
 * here rather than waiting to be noticed: a face installed from the old cut is
 * fetched again and replaced, so paragraphs already laid out re-measure. Nothing
 * else would look — a paragraph measures once and caches the answer. A pack
 * with no revision, or the same one, changes nothing.
 *
 * Nothing calls this by default: a package that shipped a pack list would ship
 * one deployment's hostnames to every consumer. Until a host calls it, the
 * engine draws the bundled families and nothing else.
 *
 * Page-wide: see "One catalogue per page" above.
 */
export function declareFontPacks(packs: readonly HostedFontPack[]): void {
    for (const pack of packs) {
        const previous = declared.get(pack.family);
        declared.set(pack.family, pack);
        for (const face of pack.faces) declareFontVariant(keyFor(pack.family, face));
        if (isRecut(previous, pack)) void reloadFontFamily(pack.family);
    }
}

/**
 * Does declaring `pack` over `previous` call for a fresh load? Yes when a face
 * of it is installed from another revision, or when an attempt is under way (or
 * has failed) for the revision declared before it. A pack that names no
 * revision cannot be told from itself, so it never does.
 */
function isRecut(previous: HostedFontPack | undefined, pack: HostedFontPack): boolean {
    if (pack.revision === undefined) return false;
    const stale = pack.faces.some((face) => {
        const k = fontRegistryKey(keyFor(pack.family, face));
        return registered.has(k) && revisions.get(k) !== pack.revision;
    });
    return stale || (loads.has(pack.family) && previous?.revision !== pack.revision);
}

/** Drop what a family's last attempt left behind and load it again. */
function reloadFontFamily(family: string): Promise<HostedFontLoadResult | null> {
    loads.delete(family);
    failures.delete(family);
    return ensureFontFamily(family);
}

/**
 * Withdraw a family this deployment declared: the inverse of
 * {@link declareFontPacks}, for one that fails for good or is retired while the
 * page is open. Without it a declaration outlives the failure it made a promise
 * about, and {@link isFamilyAvailable} keeps answering yes for a name nothing
 * can draw.
 *
 * The family stops being available, is dropped from {@link
 * declaredFontFamilies}, and any failure recorded for it clears. Faces whose
 * bytes already arrived stay: they are registered, not merely promised, and a
 * paragraph drawn in one keeps drawing. A load still on the wire when this is
 * called lands and registers as usual. Returns whether anything was withdrawn.
 */
export function undeclareFontFamily(family: string): boolean {
    const had = declared.delete(family);
    loads.delete(family);
    failures.delete(family);
    return undeclareFamilyVariants(family) || had;
}

/** Families this deployment publishes, whether or not they have loaded. */
export function declaredFontFamilies(): string[] {
    return [...declared.keys()].sort();
}

/** The declared pack for a family, if it has one. */
export function declaredFontPack(family: string): HostedFontPack | undefined {
    return declared.get(family);
}

/**
 * Make sure a declared family is on its way. Cheap and idempotent — the first
 * call for a family starts its fetches, every later one is a Map lookup — so
 * the Text behavior can call it on every measure without thinking about it.
 *
 * Returns the load. The Text behavior ignores it on purpose: the paragraph
 * must draw now, through the fallback chain, and swap when the face lands, and
 * blocking a measure on a network call would turn a hosted family into a blank
 * sign. A caller that CANNOT draw before the face is there — a bake, a headless
 * export — awaits it instead. It never rejects; it resolves once every face has
 * either landed or failed, to what that load did, or to `null` when there was
 * nothing to load (the family is not declared: bundled, or unknown). A failure
 * is also on {@link hostedFontFailures} by the time it resolves.
 *
 * Calls after the first return the same load, settled or not, including for a
 * family whose attempt failed; {@link retryFontFamily} is what tries again.
 */
export function ensureFontFamily(family: string): Promise<HostedFontLoadResult | null> {
    const existing = loads.get(family);
    if (existing) return existing;
    const pack = declared.get(family);
    if (!pack) return NOTHING_TO_LOAD;
    const load: Promise<HostedFontLoadResult> = loadFontPack(pack).then((result) => {
        settledLoads.add(load);
        // A retry, a re-cut or an undeclare replaced this attempt while it ran;
        // the newer one's bookkeeping is the one that counts.
        if (loads.get(family) !== load) return result;
        const failed = result.skipped.filter(s => s.reason === 'fetch-failed');
        if (failed.length === 0) {
            failures.delete(family);
            return result;
        }
        // Recorded as a failure, not treated as done: the family is incomplete
        // because bytes did not arrive, which is a condition that can change,
        // and `retryFontFamily` acts on the record. Treating it as final was a
        // one-shot — a boot offline, or one 503, and the face never loaded
        // again for the life of the page, with no signal but a warn.
        const failure: HostedFontPackFailure = { family, failed };
        failures.set(family, failure);
        Logger.warn(
            `[Text] hosted font "${family}": ${failed.length} of ${pack.faces.length} ` +
            `face(s) did not load. Retry with retryFontFamily(${JSON.stringify(family)}).`,
        );
        for (const listener of failureListeners) {
            try {
                listener(failure);
            } catch (error) {
                Logger.warn(`[Text] hosted font failure listener threw: ${String(error)}`);
            }
        }
        return result;
    });
    loads.set(family, load);
    return load;
}

/**
 * Is a declared family's pack still on the wire? False once every face has
 * landed or failed, and for a family that is bundled or not declared. A host
 * reads it to tell "still arriving" from "settled, possibly substituted".
 */
export function isFontFamilyLoading(family: string): boolean {
    const load = loads.get(family);
    return load !== undefined && !settledLoads.has(load);
}

/**
 * Try a failed family again: the next attempt refetches what did not arrive.
 * A family that loaded, or one still loading, has nothing to redo and gets its
 * current load back, so a retry never races the attempt it would repeat.
 *
 * Retrying is the host's call, not a timer's: how long to wait, and whether to
 * wait at all, is a deployment question, and a self-retrying loop inside a
 * measure path is how a font server gets hammered by a page that is simply
 * idle. {@link onHostedFontFailure} is the signal to act on.
 */
export function retryFontFamily(family: string): Promise<HostedFontLoadResult | null> {
    if (failures.has(family)) return reloadFontFamily(family);
    return ensureFontFamily(family);
}

/** A declared family that did not fully load, and which faces are missing. */
export interface HostedFontPackFailure {
    family: string;
    /** The faces whose bytes did not arrive. Always `reason: 'fetch-failed'`. */
    failed: Array<{ key: FontKey; reason: HostedFontSkipReason }>;
}

const failureListeners = new Set<(failure: HostedFontPackFailure) => void>();

/**
 * Subscribe to families that failed to load. Returns an unsubscribe function.
 *
 * The counterpart to {@link onFontRegistryChange}: that one fires when a face
 * lands, this one when it does not. Without it the only trace of a hosted
 * family that never arrived is a `Logger.warn` below every host's log level —
 * the silent substitution this whole area exists to remove, one layer down.
 */
export function onHostedFontFailure(
    listener: (failure: HostedFontPackFailure) => void,
): () => void {
    failureListeners.add(listener);
    return () => { failureListeners.delete(listener); };
}

/**
 * Families that failed to load, with their missing faces.
 *
 * A snapshot rather than an event, so a host that subscribes after boot — an
 * editor panel opened later, a bake asking "is this scene renderable?" — still
 * sees a failure it was not listening for. An entry clears when the family
 * loads.
 */
export function hostedFontFailures(): HostedFontPackFailure[] {
    // Copied, not handed out: the live entries are this module's own record of
    // what to retry, and a caller sorting or splicing the array it got back
    // would edit them.
    return [...failures.values()].map(f => ({
        family: f.family,
        failed: f.failed.map(entry => ({ key: { ...entry.key }, reason: entry.reason })),
    }));
}

/**
 * Drop all runtime registration bookkeeping. TEST ONLY — the registries
 * themselves are append-only by design, so this exists to keep one test file's
 * fetch-dedup state from leaking into the next test, not to unregister a face.
 *
 * Declarations survive: they are the host's catalogue, and clearing them would
 * leave `fontRegistry`'s own declared-variant list saying yes to faces this
 * module no longer knows about. The load bookkeeping is cleared so a test can
 * re-trigger a load it stubbed.
 */
export function _resetHostedFontsForTest(): void {
    listeners.clear();
    failureListeners.clear();
    inFlight.clear();
    registered.clear();
    revisions.clear();
    loads.clear();
    failures.clear();
}

